import { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Mail, Save, Send, Loader2, AlertCircle, CheckCircle, FileText, Palette } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_reply_to: '',
    admin_notification_email: ''
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    event_type: 'new',
    subject: '',
    body: '',
    is_active: true
  });

  useEffect(() => {
    loadSettings();
    loadEmailTemplates();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`, { headers: getAuthHeader() });
      setSettings({
        smtp_host: response.data.smtp_host || '',
        smtp_port: response.data.smtp_port || 587,
        smtp_username: response.data.smtp_username || '',
        smtp_password: '',
        smtp_from_email: response.data.smtp_from_email || '',
        smtp_reply_to: response.data.smtp_reply_to || '',
        admin_notification_email: response.data.admin_notification_email || ''
      });
    } catch (error) {
      console.error('Load settings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/email-templates`, { headers: getAuthHeader() });
      setEmailTemplates(response.data);
    } catch (error) {
      console.error('Load templates error:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = { ...settings };
      if (!dataToSave.smtp_password) {
        delete dataToSave.smtp_password;
      }
      
      await axios.put(`${API_URL}/api/settings`, dataToSave, { headers: getAuthHeader() });
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }
    
    setTestingEmail(true);
    try {
      await axios.post(
        `${API_URL}/api/settings/test-email?to_email=${encodeURIComponent(testEmail)}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Test-E-Mail erfolgreich gesendet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test-E-Mail fehlgeschlagen');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (editingTemplate) {
        await axios.put(`${API_URL}/api/email-templates/${editingTemplate.id}`, templateForm, { headers: getAuthHeader() });
        toast.success('Vorlage aktualisiert');
      } else {
        await axios.post(`${API_URL}/api/email-templates`, templateForm, { headers: getAuthHeader() });
        toast.success('Vorlage erstellt');
      }
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', event_type: 'new', subject: '', body: '', is_active: true });
      loadEmailTemplates();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    try {
      await axios.delete(`${API_URL}/api/email-templates/${id}`, { headers: getAuthHeader() });
      toast.success('Vorlage gelöscht');
      loadEmailTemplates();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      event_type: template.event_type,
      subject: template.subject,
      body: template.body,
      is_active: template.is_active
    });
    setTemplateDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Einstellungen</h1>
          <p className="text-gray-500 mt-1">System- und E-Mail-Konfiguration</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Speichern
        </Button>
      </div>

      <Tabs defaultValue="smtp" className="space-y-4">
        <TabsList>
          <TabsTrigger value="smtp">
            <Mail className="h-4 w-4 mr-2" />
            SMTP
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileText className="h-4 w-4 mr-2" />
            E-Mail-Vorlagen
          </TabsTrigger>
          <TabsTrigger value="general">
            <SettingsIcon className="h-4 w-4 mr-2" />
            Allgemein
          </TabsTrigger>
        </TabsList>

        <TabsContent value="smtp">
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>SMTP-Konfiguration</CardTitle>
                <CardDescription>Konfigurieren Sie Ihren E-Mail-Server</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>SMTP Host</Label>
                    <Input
                      value={settings.smtp_host}
                      onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                      placeholder="smtp.example.com"
                    />
                  </div>
                  <div>
                    <Label>SMTP Port</Label>
                    <Input
                      type="number"
                      value={settings.smtp_port}
                      onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Benutzername</Label>
                    <Input
                      value={settings.smtp_username}
                      onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                      placeholder="user@example.com"
                    />
                  </div>
                  <div>
                    <Label>Passwort</Label>
                    <Input
                      type="password"
                      value={settings.smtp_password}
                      onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Absender E-Mail</Label>
                    <Input
                      type="email"
                      value={settings.smtp_from_email}
                      onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                      placeholder="noreply@example.com"
                    />
                  </div>
                  <div>
                    <Label>Reply-To</Label>
                    <Input
                      type="email"
                      value={settings.smtp_reply_to}
                      onChange={(e) => setSettings({ ...settings, smtp_reply_to: e.target.value })}
                      placeholder="info@example.com"
                    />
                  </div>
                </div>

                <div>
                  <Label>Admin-Benachrichtigungs-E-Mail</Label>
                  <Input
                    type="email"
                    value={settings.admin_notification_email}
                    onChange={(e) => setSettings({ ...settings, admin_notification_email: e.target.value })}
                    placeholder="admin@example.com"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Test</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="max-w-sm"
                  />
                  <Button onClick={handleTestEmail} disabled={testingEmail} variant="outline">
                    {testingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Test senden
                  </Button>
                </div>
                {!settings.smtp_host && (
                  <Alert className="mt-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>SMTP nicht konfiguriert.</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>E-Mail-Vorlagen</CardTitle>
                  <CardDescription>Vorlagen für automatische Benachrichtigungen</CardDescription>
                </div>
                <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', event_type: 'new', subject: '', body: '', is_active: true }); }}>
                      Neue Vorlage
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>{editingTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Name</Label>
                          <Input value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} />
                        </div>
                        <div>
                          <Label>Ereignis</Label>
                          <Select value={templateForm.event_type} onValueChange={(v) => setTemplateForm({ ...templateForm, event_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">Neue Einreichung</SelectItem>
                              <SelectItem value="approved">Genehmigt</SelectItem>
                              <SelectItem value="rejected">Abgelehnt</SelectItem>
                              <SelectItem value="reminder">Erinnerung</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Betreff</Label>
                        <Input value={templateForm.subject} onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })} placeholder="Neue Einreichung: {{form_name}}" />
                      </div>
                      <div>
                        <Label>Inhalt (HTML)</Label>
                        <Textarea value={templateForm.body} onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })} rows={8} placeholder="<h2>Neue Einreichung</h2><p>Formular: {{form_name}}</p>" />
                        <p className="text-xs text-gray-500 mt-1">Platzhalter: {"{{form_name}}, {{status}}, {{submitted_at}}, {{submission_id}}"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={templateForm.is_active} onCheckedChange={(v) => setTemplateForm({ ...templateForm, is_active: v })} />
                        <Label>Aktiv</Label>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Abbrechen</Button>
                      <Button onClick={handleSaveTemplate}>Speichern</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {emailTemplates.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Keine Vorlagen vorhanden</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Ereignis</TableHead>
                      <TableHead>Betreff</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailTemplates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell className="font-medium">{template.name}</TableCell>
                        <TableCell>{template.event_type}</TableCell>
                        <TableCell className="max-w-xs truncate">{template.subject}</TableCell>
                        <TableCell>{template.is_active ? <span className="text-green-600">Aktiv</span> : <span className="text-gray-400">Inaktiv</span>}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => openEditTemplate(template)}>Bearbeiten</Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteTemplate(template.id)}>Löschen</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Allgemeine Einstellungen</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>Weitere Einstellungen werden in zukünftigen Updates verfügbar.</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
